import React, { useMemo } from "react";
import { allCCTState, newMissionState } from "../../../atoms/core";
import { MockedProvider } from "@apollo/client/testing/react";
import CreateMissionSalary from "../../../screens/create-mission/CreateMissionSalary";
import {
    Cct,
    EarningType,
    LegalEmployer,
    LppPlan,
    ProfileType,
    Repetition,
    Unit,
} from "../../../__generated__/graphql";
import { RootStackParamList } from "../../../navigations/RootNavigation";
import type { Meta, StoryObj } from "@storybook/react";
import {
    buildCct,
    buildClient,
    buildClientInfo,
    buildClientProfile,
    buildContract,
    buildIjmGroup,
    buildLegalEmployer,
    buildLppPlan,
    buildMission,
    buildUser,
    buildWorkerInfo,
    IJM_CHOICES_MOCK,
} from "../../factories";
import { buildGraphqlMock_GET_SALARY_DETAILS } from "../../mock-factories";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import SalaryCountingDetailsScreen from "../../../screens/SalaryCountingDetails";
import { addDays } from "date-fns";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import CreateMissionManager from "../../../contexts/CreateMissionManager";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import IJMEditorNavScreen from "../../../screens/client/IJMEditorNavScreen";
import { StoryStackNavigatorContainer } from "../../display-helpers";
import { AtomsHydrator } from "../../../atoms/atoms-hydrator";
import { Buildable } from "ts-essentials";
import { IjmSelection } from "../../../utils";
import { GET_CCT_BY_EDITION_BY } from "../../../graphql/cct";
import { GET_LPP_PLANS } from "../../../graphql/salaries";
import LPPEditorNavScreen from "../../../screens/client/LPPEditorNavScreen";
import ThirteenthSalaryEditorNavScreen from "../../../screens/client/ThirteenthSalaryEditorNavScreen";

type StoryStackParams = {
    CreateMissionSalary: undefined;
    IJMEditorNavScreen: {
        ijmSelection?: Buildable<IjmSelection> | undefined;
        salaryInfo?:
            | {
                  amount: number;
                  earningType: EarningType;
              }
            | undefined;
        cct?: Cct;
        useLse: boolean;
    };
    LPPEditorNavScreen: {
        lppPlan?: Buildable<LppPlan> | undefined;
        cct?: Cct;
        useLse: boolean;
        availablePlans: LppPlan[];
    };
    SalaryCountingDetails: RootStackParamList["SalaryCountingDetails"];
    ThirteenthSalaryEditorNavScreen: RootStackParamList["ThirteenthSalaryEditorNavScreen"];
};

const Stack = createNativeStackNavigator<StoryStackParams>();

const WORKING_TIME_CLASSIC_SPEC = {
    [EarningType.Hourly]: {
        unit: Unit.Hours,
        unitCount: 10,
        repetition: Repetition.Week2,
    },
    [EarningType.Daily]: {
        unit: Unit.Days,
        unitCount: 5,
        repetition: Repetition.Month,
    },
    [EarningType.Monthly]: {
        unit: Unit.Hours,
        unitCount: 21,
        repetition: Repetition.Week,
    },
    [EarningType.Package]: {
        unit: Unit.Hours,
        unitCount: 32,
        repetition: Repetition.None,
    },
};

const SALARIES = {
    [EarningType.Hourly]: 3500,
    [EarningType.Daily]: 45000,
    [EarningType.Monthly]: 800000,
    [EarningType.Package]: 300000,
};

function StoryComponent({
    earningType,
    duration,
    hasIjm,
    hasSalary = true,
    hasLppPlan = true,
    canChangeIjmGroup = false,
    legalEmployer = buildLegalEmployer(),
}: {
    earningType: EarningType;
    duration: number;
    hasIjm: boolean;
    hasSalary: boolean;
    hasLppPlan?: boolean;
    canChangeIjmGroup: boolean;
    legalEmployer: LegalEmployer;
}) {
    const mocks = useMemo(
        () => [
            buildGraphqlMock_GET_SALARY_DETAILS(),
            IJM_CHOICES_MOCK,
            {
                request: {
                    query: GET_LPP_PLANS,
                    variables: {
                        cctContractId: "815001",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => {
                    const result = {
                        data: {
                            getLppPlans: [
                                buildLppPlan({
                                    id: "plan-1",
                                    name: "Plan 1",
                                    description: "Plan 1 description",
                                }),
                                buildLppPlan({
                                    id: "plan-2",
                                    name: "Plan 2",
                                    description: "Plan 2 description",
                                }),
                                buildLppPlan({
                                    id: "plan-3",
                                    name: "Plan 3",
                                    description: "Plan 3 description",
                                }),
                            ],
                        },
                    };
                    return result;
                },
            },
            {
                request: {
                    query: GET_CCT_BY_EDITION_BY,
                    variables: {
                        cctEditionId: "1",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => ({
                    data: {
                        getCctByEditionId: buildCct({
                            name: "Super CCT",
                            cctContractId: "815001",
                            cctEditionId: "12071",
                            cctVersionId: "9",
                            isIjmMandatory: true,
                        }),
                    },
                }),
            },
        ],
        [],
    );
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client", useLse: legalEmployer.useLseCct })],
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        client: buildClient({
                            ijmGroup: buildIjmGroup({
                                id: "GM-G1-730-2",
                            }),
                            ijmPayFirstDays: true,
                            ijmPayRemainingPercents: 10,
                            canChangeIjmGroup,
                            useLegalEmployer: legalEmployer,
                        }),
                    }),
                ],
            }),
        [canChangeIjmGroup, legalEmployer],
    );
    const ijmGroup = useMemo(
        () =>
            buildIjmGroup({
                id: "GM-G1-730-2",
            }),
        [],
    );

    const mission = useMemo(
        () =>
            buildMission({
                workerInfo: buildWorkerInfo({
                    id: "worker",
                    birthdate: "1975-05-12",
                }),
                currentContract: buildContract({
                    earningType: earningType,
                    startDate: new Date().toISOString(),
                    endDate: addDays(new Date(), duration).toISOString(),
                    workingTimeClassicSpec: {
                        ...WORKING_TIME_CLASSIC_SPEC[earningType],
                        //unitCount: 42,
                        dailyHours: null,
                    },
                    grossSalary: hasSalary ? SALARIES[earningType] : null,
                    hoursPerWeekFullTime: 42,
                    // without a selected plan the screen renders the mandatory LPP
                    // selection state ("Sélectionner le plan") and blocks the salary
                    lppPlan: hasLppPlan
                        ? buildLppPlan({
                              id: "plan-1",
                              name: "Plan 1",
                              description: "Plan 1 description",
                          })
                        : undefined,
                    ijmInfo: hasIjm
                        ? {
                              ijmGroup: ijmGroup,
                              payFirstDays: false,
                              payRemainingPercents: 10,
                          }
                        : null,
                }),
            }),
        [duration, earningType, hasIjm, hasSalary, hasLppPlan, ijmGroup],
    );
    const cctList = useMemo(
        () => [
            buildCct({
                name: "Super CCT",
            }),
        ],
        [],
    );
    return (
        <AtomsHydrator
            atomValues={[
                [newMissionState, mission],
                [allCCTState, cctList],
            ]}
        >
            <MockedProvider mocks={mocks}>
                <MockedAuthManager user={user}>
                    <MockedProfileSwitcherManager
                        profileType={ProfileType.Client}
                        profileId={ProfileType.Client}
                    >
                        <CreateMissionManager
                            testingInitializedData={{
                                ...mission,
                                ijmSelection: hasIjm
                                    ? {
                                          ijmGroupId: ijmGroup.id,
                                          ijmGroupName: ijmGroup.name,
                                          ijmPayFirstDays: false,
                                          ijmPayRemainingPercents: 10,
                                      }
                                    : {},
                            }}
                        >
                            <StoryStackNavigatorContainer>
                                <Stack.Navigator initialRouteName="CreateMissionSalary">
                                    <Stack.Screen
                                        name="CreateMissionSalary"
                                        component={CreateMissionSalary}
                                        options={{
                                            gestureEnabled: false,
                                            headerShown: true,
                                            headerBackButtonDisplayMode: "default",
                                        }}
                                    />
                                    <Stack.Screen
                                        name="SalaryCountingDetails"
                                        component={SalaryCountingDetailsScreen}
                                        options={{
                                            headerShown: false,
                                            gestureEnabled: true,
                                            presentation: "modal",
                                        }}
                                    />
                                    <Stack.Screen
                                        name="IJMEditorNavScreen"
                                        component={IJMEditorNavScreen}
                                        options={{
                                            headerShown: false,
                                            gestureEnabled: true,
                                            animation: "slide_from_bottom",
                                        }}
                                    />
                                    <Stack.Screen
                                        name="LPPEditorNavScreen"
                                        component={LPPEditorNavScreen}
                                        options={{
                                            headerShown: false,
                                            gestureEnabled: true,
                                            animation: "slide_from_bottom",
                                        }}
                                    />
                                    <Stack.Screen
                                        name="ThirteenthSalaryEditorNavScreen"
                                        component={ThirteenthSalaryEditorNavScreen}
                                        options={{
                                            headerShown: false,
                                            gestureEnabled: true,
                                            animation: "slide_from_bottom",
                                        }}
                                    />
                                </Stack.Navigator>
                            </StoryStackNavigatorContainer>
                        </CreateMissionManager>
                    </MockedProfileSwitcherManager>
                </MockedAuthManager>
            </MockedProvider>
        </AtomsHydrator>
    );
}

const meta = {
    title: "CreateMission/Salary",
    component: StoryComponent,
    argTypes: {
        earningType: {
            options: [EarningType.Hourly, EarningType.Monthly],
            control: { type: "radio" },
        },
        duration: {
            options: [1, 30],
            value: {
                defaultValue: 30,
            },
            control: { type: "radio" },
        },
    },
    args: {
        canChangeIjmGroup: true,
        legalEmployer: buildLegalEmployer(),
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Hourly: Story = {
    args: {
        earningType: EarningType.Hourly,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const Monthly: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const MonthlyLppSelectionRequired: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
        hasLppPlan: false,
    },
};

export const MonthlyNoLSE: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
        legalEmployer: buildLegalEmployer({
            useLseCct: false,
        }),
    },
};

export const Daily: Story = {
    args: {
        earningType: EarningType.Daily,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const Package: Story = {
    args: {
        earningType: EarningType.Package,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const PackageShort: Story = {
    args: {
        earningType: EarningType.Package,
        duration: 0,
        hasIjm: true,
        hasSalary: true,
        canChangeIjmGroup: false,
    },
};

export const MonthlyNoIjm: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 0,
        hasSalary: true,
        hasIjm: false,
    },
};

export const MonthlyEmpty: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 0,
        hasIjm: false,
        hasSalary: false,
    },
};
